/* eslint-disable @typescript-eslint/no-explicit-any */
'use strict';

import React, { useCallback, useRef, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';

import ajax from './ajax';
import MediaViewer from './MediaViewer';
import Modal from 'react-modal';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../logic/cropImage';
import { debounce } from 'lodash';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import auth from './auth';
import { InfluencerIdContext } from '@client/new/contexts/InfluencerIdContext';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';

type MediaEditorProps = {
  id?: unknown;
  onChange: any; // two type signatures
  accept?: string;
  metadata: unknown;
  onRemove?: any;
  onDelete?: any;
  format: any;
  className?: string;
  crop?: boolean;
  width?: string;
  height?: string;
  aspect?: number;
};
type MediaEditorState = {
  files?: Array<File>;
  preview: null | unknown;
  message: string;
  inProgress: boolean;
  cropModal: boolean;
  croppedImageUrl: null | string;
};
class MediaEditor extends React.Component<MediaEditorProps, MediaEditorState> {
  static INITIAL_STATE: Readonly<MediaEditorState> = {
    files: [],
    preview: null,
    message: 'Drop or',
    inProgress: false,
    cropModal: false,
    croppedImageUrl: null,
  };

  declare context: React.ContextType<typeof InfluencerIdContext>;
  static contextType = InfluencerIdContext;

  constructor(props) {
    super(props);

    this.state = MediaEditor.INITIAL_STATE;
  }

  onOpenClick = () => {
    // eslint-disable-next-line react/no-string-refs
    (this.refs?.dropzone as any)?.open();
  };

  onDrop = (files: File[]) => {
    const videoSizeLimit = maxFileSizeBytes;

    if (files[0].size > videoSizeLimit) {
      this.setState({
        files: [],
        message: 'File too large. Max file size is 100MB',
        inProgress: false,
      });
      return;
    }

    const croppingEnabled = this.props.crop;
    this.setState({
      files: files,
      message: 'Uploading...',
      inProgress: true,
    });

    if (croppingEnabled) {
      this.setState({ cropModal: croppingEnabled });

      return;
    }

    this.onDropContinue(files);
  };

  onCropCancel = () => {
    this.setState({ ...MediaEditor.INITIAL_STATE });
  };

  onCropSave = async () => {
    const croppedImageUrl = this.state.croppedImageUrl;

    if (!croppedImageUrl) {
      return;
    }
    this.setState({ cropModal: false });

    const newBlob = await fetch(croppedImageUrl).then((r) => r.blob());
    const oldFile = this.state.files[0];

    this.onDropContinue([new File([newBlob], oldFile.name, oldFile)]);
  };

  onDropContinue = (files: File[]) => {
    if (this.props.id) this.props.onChange(this.props.id, null);
    else this.props.onChange(null);
    const influencerId = this.context;

    ajax
      .upload(files[0], influencerId)
      .then((metadata) => {
        //console.log('[DropMediaEditor] Uploaded file', f);
        this.setState({
          files: files,
          message: 'Drop or',
          inProgress: false,
        });
        if (this.props.id) this.props.onChange(this.props.id, metadata);
        else this.props.onChange(metadata);
      })
      .catch((e) => {
        //if too large
        if (e.status === 413) {
          this.setState({
            files: [],
            message: 'File too large. Please drop again or',
            inProgress: false,
          });
          return;
        } else if (e.status === 400) {
          this.setState({
            files: [],
            message: 'Unsupported file format. Please drop again or',
            inProgress: false,
          });
          return;
        }
        console.log('[MediaEditor] Error uploading file.', e);
        this.setState({
          files: [],
          message: 'Woops, we encountered an error. Please drop again or',
          inProgress: false,
        });
      });
  };

  render() {
    const isValidContext =
      typeof this.context === 'number' && this.context >= 0;
    if (!isValidContext) {
      throw new Error(
        'InfluencerIdContext is not set! Make sure to wrap this MediaEditor with InfluencerIdProvider.',
      );
    }
    const browseLink = this.state.inProgress ? (
      ''
    ) : (
      <a onClick={this.onOpenClick} className="actionLink mark">
        browse.
      </a>
    );
    const deleteLink =
      this.props.onDelete || (this.props.onRemove && this.props.metadata) ? (
        //<a className='actionLink close-button' onClick={() => {
        <a
          className="actionLink icon-remove"
          onClick={() => {
            if (this.props.onDelete) {
              this.props.onDelete(this.props.id); // for multiple media editor
            } else if (this.props.onRemove) {
              this.props.onRemove(null);
            }
          }}
        ></a>
      ) : null;

    let content = (
      <span className="dropzoneBanner download-title">
        {this.state.message}&nbsp;
        {browseLink}
      </span>
    );
    if (this.props.metadata) {
      content = (
        <div>
          <MediaViewer
            className={this.props.className}
            metadata={this.props.metadata}
            width={this.props.width}
            height={this.props.height}
          />
        </div>
      );
    } else if (this.props.format) {
      content = (
        <span className={`dropzoneBanner ${this.props.format} download-title`}>
          {this.state.message}&nbsp;
          {browseLink}
        </span>
      );
    }

    return (
      <div
        className="dropMediaEditor"
        style={{ width: '100%', height: '100%' }}
      >
        <Modal
          isOpen={this.state.cropModal}
          onRequestClose={this.onCropCancel}
          contentLabel="Example Modal"
          style={{
            overlay: {
              zIndex: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            content: {
              paddingRight: '40px',
              paddingLeft: '40px',
              paddingBottom: '40px',
              backgroundColor: 'black',
              position: 'relative',
              width: '80vw',
              height: '60vh',
              borderRadius: '20px',
            },
          }}
        >
          <a
            onClick={this.onCropCancel}
            className="absolute right-6 text-3xl text-white hover:text-primary hover:cursor-pointer"
          >
            <FontAwesomeIcon icon={faClose} />
          </a>
          <div className="text-2xl text-white text-center font-normal mb-4">
            Move the image to fit into highlighted area
          </div>
          <div
            className="relative"
            style={{
              width: '100%',
              height: 'calc(100% - 140px)',
              // background: 'white',
            }}
          >
            <CropImage
              src={(this.state.files[0] as any)?.preview}
              onChange={(croppedImageUrl) => this.setState({ croppedImageUrl })}
              aspect={this.props.aspect}
            />
          </div>
          <div className="w-full text-center">
            <button
              onClick={this.onCropSave}
              disabled={!this.state.croppedImageUrl}
              className="bg-primary text-white rounded-lg text-2xl mt-8 p-4 px-20"
            >
              Upload
            </button>
          </div>
        </Modal>
        <Dropzone
          // eslint-disable-next-line react/no-string-refs
          ref="dropzone"
          onDrop={this.onDrop}
          className="dropzone"
          activeClassName="dropzone-active"
          disableClick={true}
          style={{ height: '100%', width: '100%' }}
          accept={this.props.accept}
          multiple={!this.props.crop}
        >
          {deleteLink}
          {content}
        </Dropzone>
      </div>
    );
  }
}

export default MediaEditor;

function useEasyCrop({ src, aspect = 1, onChange }): Cropper['props'] {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [_croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [zoom, setZoom] = useState(1);

  const onChangeHandlerDebounced = useCallback(
    debounce(async (croppedAreaPixels) => {
      console.log('onChangeHandler', new Date());
      const croppedImage = await getCroppedImg(
        src,
        croppedAreaPixels,
        rotation,
      );
      onChange(croppedImage);
    }, 200),
    [],
  );

  const onCropComplete = useCallback(
    async (_croppedArea, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
      onChange(null);
      // NOTE: most likely doesn't support rotation after cropping
      onChangeHandlerDebounced(croppedAreaPixels);
    },
    [],
  );

  // @ts-ignore
  return {
    image: src,
    crop,
    rotation,
    onCropChange: setCrop,
    onCropComplete,
    onRotationChange: setRotation,
    aspect,
    zoomWithScroll: true,
    onZoomChange: setZoom,
    zoom,
  };
}

function CropImage({ src, aspect = 1, onChange }: any) {
  const cropper = useEasyCrop({ src, aspect, onChange });

  return (
    <div>
      <Cropper {...cropper} />
    </div>
  );
}
